/** Recipe line-item units — g drives price; tsp/drops are display-only on the card. */
export const RECIPE_AMOUNT_UNITS = ["g", "tsp", "drops"];

/** Teaspoon choices (fractions, not decimals). */
export const TSP_FRACTION_OPTIONS = [
  { value: "none", label: "—" },
  { value: "1/8", label: "1/8 tsp" },
  { value: "1/4", label: "1/4 tsp" },
  { value: "1/2", label: "1/2 tsp" },
];

const WASTAGE_MULTIPLIER = 1.08;
/** Essence lines priced per kg when recipe stores tsp only */
const ESSENCE_GRAMS_PER_TSP = 5;

const emptyAmounts = () => ({ g: 0, tsp: "", drops: 0 });

export function parseFractionToNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const s = String(value).trim();
  if (!s) return 0;
  if (s.includes("/")) {
    const [num, den] = s.split("/");
    const n = parseFloat(num);
    const d = parseFloat(den);
    if (Number.isFinite(n) && Number.isFinite(d) && d !== 0) return n / d;
    return 0;
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

/** Map legacy decimal tsp (e.g. 1.2) to nearest common fraction label for edit UI. */
export function normalizeTspFractionLabel(value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string" && value.includes("/")) return value.trim();
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  const candidates = [
    ["1/8", 1 / 8],
    ["1/4", 1 / 4],
    ["1/2", 1 / 2],
  ];
  let best = "";
  let bestDiff = Infinity;
  candidates.forEach(([label, val]) => {
    const diff = Math.abs(n - val);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = label;
    }
  });
  return bestDiff < 0.06 ? best : "";
}

export function parseIngredientAmounts(ing) {
  if (!ing) return emptyAmounts();

  if (ing.amounts && typeof ing.amounts === "object") {
    const tspRaw = ing.amounts.tsp;
    return {
      g: parseFloat(ing.amounts.g) || 0,
      tsp: normalizeTspFractionLabel(tspRaw),
      drops: Math.round(parseFloat(ing.amounts.drops) || 0),
    };
  }

  const amounts = emptyAmounts();
  const unit = String(ing.unit || "g").trim().toLowerCase();
  const q = ing.quantity ?? ing.grams;
  if (unit === "tsp" || unit === "tsps") {
    amounts.tsp = normalizeTspFractionLabel(q);
  } else if (unit === "drop" || unit === "drops") {
    amounts.drops = Math.round(parseFloat(q) || 0);
  } else {
    amounts.g = parseFloat(ing.grams) || parseFloat(q) || 0;
  }
  return amounts;
}

/** Grams used for inventory cost — g field only. */
export function ingredientGramsForCost(ing) {
  return parseIngredientAmounts(ing).g;
}

export function buildIngredientCatalog(priceList) {
  const catalog = {};
  (priceList || []).forEach((ing) => {
    const trimmedName = String(ing?.name || "").trim();
    if (!trimmedName) return;
    const key = trimmedName.toLowerCase();
    catalog[key] = {
      name: trimmedName,
      price: parseFloat(ing.price_per_kg) || 0,
      unit: String(ing.unit || "kg").trim().toLowerCase(),
    };
  });
  return catalog;
}

export function lookupCatalogEntry(catalog, name) {
  const key = String(name || "").trim().toLowerCase();
  if (!key) return null;
  return catalog[key] || null;
}

export function formatIngredientCostInr(cost) {
  const n = Number(cost) || 0;
  if (n <= 0) return "0.00";
  if (n < 0.01) return n.toFixed(3);
  return n.toFixed(2);
}

export function inventoryPricePerGram(price, inventoryUnit) {
  const u = String(inventoryUnit || "kg").trim().toLowerCase();
  if (u === "kg") return price / 1000;
  if (u === "g" || u === "gram" || u === "grams") return price;
  if (u === "litre" || u === "liter" || u === "l") return price / 1000;
  if (u === "ml") return price;
  return price / 1000;
}

export function getRecipeIngredientCost(ing, catalog) {
  const amounts = parseIngredientAmounts(ing);
  const entry = lookupCatalogEntry(catalog, ing.name);
  if (!entry) return 0;
  const pricePerGram = inventoryPricePerGram(entry.price, entry.unit);
  let total = pricePerGram * (amounts.g || 0);
  if (amounts.tsp) {
    const tspQty = parseFractionToNumber(amounts.tsp);
    if (tspQty > 0) {
      total += pricePerGram * tspQty * ESSENCE_GRAMS_PER_TSP;
    }
  }
  return total;
}

export function calculateRecipeIngredientsCost(ingredients, catalog, weightMultiplier = 1) {
  if (!ingredients?.length) return 0;
  let total = 0;
  ingredients.forEach((ing) => {
    total += getRecipeIngredientCost(ing, catalog);
  });
  return total * weightMultiplier * WASTAGE_MULTIPLIER;
}

export function formatIngredientAmount(ing) {
  const a = parseIngredientAmounts(ing);
  const parts = [];
  if (a.g > 0) parts.push(`${formatQty(a.g)}g`);
  if (a.tsp) parts.push(`${a.tsp} tsp`);
  if (a.drops > 0) {
    parts.push(a.drops === 1 ? "1 drop" : `${a.drops} drops`);
  }
  if (parts.length === 0) return "—";
  return parts.join(" + ");
}

function formatQty(n) {
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/\.?0+$/, "");
}

export function hasAnyIngredientAmount(ing) {
  const a = parseIngredientAmounts(ing);
  return a.g > 0 || Boolean(a.tsp) || a.drops > 0;
}

export function normalizeRecipeIngredientForEdit(ing) {
  const amounts = parseIngredientAmounts(ing);
  return {
    ...ing,
    section: ing?.section,
    amounts,
    grams: amounts.g,
  };
}

export function serializeRecipeIngredient(ing) {
  const name = String(ing.name || "").trim();
  const section = ing?.section === "frosting" ? "frosting" : "baking";
  const amounts = parseIngredientAmounts(ing);

  const payload = {
    name,
    section,
    grams: amounts.g,
    amounts: {},
  };

  if (amounts.g > 0) payload.amounts.g = amounts.g;
  if (amounts.tsp) payload.amounts.tsp = amounts.tsp;
  if (amounts.drops > 0) payload.amounts.drops = amounts.drops;

  return payload;
}

/** Detect tsp/drops lost between save payload and API response (regression guard). */
export function findLostRecipeIngredientAmounts(sentIngredients, receivedIngredients) {
  const receivedByName = new Map();
  (receivedIngredients || []).forEach((ing) => {
    const key = String(ing?.name || "")
      .trim()
      .toLowerCase();
    if (key) receivedByName.set(key, parseIngredientAmounts(ing));
  });

  const lost = [];
  (sentIngredients || []).forEach((ing) => {
    const name = String(ing?.name || "").trim();
    if (!name) return;
    const sent = parseIngredientAmounts(ing);
    if (!sent.tsp && !(sent.drops > 0)) return;

    const recv = receivedByName.get(name.toLowerCase());
    if (!recv) {
      lost.push(name);
      return;
    }
    if (sent.tsp && !recv.tsp) lost.push(`${name} (tsp)`);
    if (sent.drops > 0 && !(recv.drops > 0)) lost.push(`${name} (drops)`);
  });
  return lost;
}

export function newRecipeIngredientRow(section = "baking") {
  return {
    name: "",
    section,
    amounts: emptyAmounts(),
    grams: 0,
  };
}
